/**
 * An AG-Grid View that shows data loaded in from the spend API
 */
import React from 'react';
import moment from 'moment';
import { AgGridReact } from '@ag-grid-community/react';
import { AllModules } from '@ag-grid-enterprise/all-modules';

import memoize from 'memoize-async';
import isEqual from 'lodash/isEqual';
import { createServerSideDatasource } from 'Explore/TableView/graphQLDataSource';

import {
	getSpendEditData,
	getHarmonizedData,
	getTaxonomies,
	getClassificationOverrideRules,
} from 'utils/apiRequests';
import { setLocalStorageItem, getLocalStorageItem } from 'utils/localStorage';
import { compareDates } from 'utils/date';
import {
	EMPTY_VALUE,
	EMPTY_VALUE_KEY,
	EMPTY_VALUE_NULL,
	EMPTY_VALUE_SPACE,
} from 'Details/utils';

import { PERMISSIONS } from 'contexts/PermissionsContext';

import {
	COLUMNS,
	TYPES,
	TYPES_CELLS_MAP,
	chartFilterColumns,
	COLUMN_DEFS,
	FILTER_TYPES,
} from 'Explore/tableMetadata';
import { Wrapper } from './styles';
import {
	CLIENT_DEFAULT_COLUMN_STATE,
	AP_DEFAULT_COLUMN_STATE,
} from './constants';

const defaultComparator = (a, b) => {
	if ((a === null && b === null) || (a === undefined && b === undefined)) {
		return 0;
	}

	if (a === null || a === undefined) {
		return -1;
	}
	if (b === null || b === undefined) {
		return 1;
	}

	if (typeof a === 'string') {
		return a.localeCompare(b);
	} else {
		return a > b ? 1 : a < b ? -1 : 0;
	}
};

const memoizedApi = {
	getSpendEditData: memoize(getSpendEditData),
	getTaxonomies: memoize(getTaxonomies),
};

const GRID_NAME = 'AG_GRID_PREVIEW';
const WRAPPER_THEME_STYLES = {
	height: '100%',
	width: '100vw',
};

// taken from:
// https://www.ag-grid.com/javascript-grid-filter-text/
// filter blank values in a special way
function customComparator(filter, value, filterText) {
	var filterTextLowerCase = filterText.toLowerCase();
	var valueLowerCase = value.toString().toLowerCase();

	const blankMatch =
		filterTextLowerCase === EMPTY_VALUE.toLowerCase() && value === '';

	switch (filter) {
		case 'contains':
			if (blankMatch) {
				return true;
			}
			return valueLowerCase.indexOf(filterTextLowerCase) >= 0;
		case 'notContains':
			return valueLowerCase.indexOf(filterTextLowerCase) === -1;
		case 'equals':
			if (blankMatch) {
				return true;
			}
			return valueLowerCase === filterTextLowerCase;
		case 'notEqual':
			return valueLowerCase !== filterTextLowerCase;
		case 'startsWith':
			return valueLowerCase.indexOf(filterTextLowerCase) === 0;
		case 'endsWith':
			var index = valueLowerCase.lastIndexOf(filterTextLowerCase);
			return (
				index >= 0 &&
				index === valueLowerCase.length - filterTextLowerCase.length
			);
		default:
			// should never happen
			console.warn('invalid filter type ' + filter);
			return false;
	}
}

export class TableView extends React.Component {
	areFiltersApplied = false;
	state = {
		loadingMessageIndex: 0,
		columnDefs: null,
		dataRequested: false,
		defaultColDef: {
			sortable: true,
			resizable: true,
			editable: false,
			filter: true,
			enablePivot: true,
			comparator: defaultComparator,
		},
		harmonizedOptions: [],
		taxonomies: null,
		rowSelection: 'multiple',
		suppressRowClickSelection: false,
		undos: [],
		isSaving: false,
	};
	filtersData = this.props.filtersData;

	booleanCellCollDef = {
		cellEditor: 'agSelectCellEditor',
		cellEditorParams: {
			values: ['true', 'false'],
		},
	};

	currencyCellCollDef = {
		cellEditor: 'agSelectCellEditor',
		cellEditorParams: {
			values: ['USD', 'EUR', 'GBP'],
		},
	};

	notEditableCellCollDef = {
		editable: false,
	};

	componentDidMount() {
		this.fetchData();
	}

	fetchData = (props = this.props) => {
		const { setIsReady } = props;

		setIsReady(false);

		Promise.all([
			getHarmonizedData(),
			memoizedApi.getTaxonomies(),
			getClassificationOverrideRules(),
		])
			.then(([harmonizedOptions, taxonomyData, overrideRulesData]) => {
				this.setState({
					harmonizedOptions,
					taxonomies: taxonomyData.taxonomies,
					overrideRulesMap: this.getOverrideRulesMap(overrideRulesData),
				});
				this.buildAgGridData();
			})
			.catch(e => {
				console.error(e);
			});
	};

	getOverrideRulesMap = overrideRulesData => {
		const map = {};

		overrideRulesData
			.filter(overrideRule => !overrideRule.isDeleted)
			.forEach(overrideRule => {
				map[overrideRule.harmonizedVendorId] = overrideRule.id;
			});

		return map;
	};

	/**
	 * Takes the data loaded in from the API and rebuilds it to work with AG-Grid, the examples show ag grid schema.
	 *
	 * @param {object} data - request data response, an object with a data property
	 */
	buildAgGridData = () => {
		const columnDefs = COLUMN_DEFS.map(columnDef => {
			const columnDefCustom = this.getColDefOptions(columnDef);
			const childrenProp = columnDef.children
				? {
						children: columnDef.children.map(childColumnDef => ({
							...childColumnDef,
							...this.getColDefOptions(childColumnDef),
							filter:
								FILTER_TYPES[childColumnDef.field] || 'agTextColumnFilter',
						})),
				  }
				: {};

			return {
				...columnDef,
				...columnDefCustom,
				...childrenProp,
				filter: FILTER_TYPES[columnDef.field] || 'agTextColumnFilter',
			};
		});

		this.setState({ columnDefs });
	};

	getColDefOptions(columnDef) {
		const { permissions, isUnpublished, customFilterOptions } = this.props;
		const key = columnDef.field;

		if (
			isUnpublished &&
			!permissions.includes(PERMISSIONS.UnpublishedDataWrite)
		) {
			return this.notEditableCellCollDef;
		}

		if (
			!isUnpublished &&
			!permissions.includes(PERMISSIONS.TransactionsWrite)
		) {
			return this.notEditableCellCollDef;
		}

		let editingColDef = {};

		// Add header labels to custom_data columns
		const customFilterItem =
			customFilterOptions &&
			customFilterOptions.find(option => option.columnName === key);

		if (customFilterItem && customFilterItem.label) {
			editingColDef.headerName = customFilterItem.label;
		}

		if (TYPES_CELLS_MAP[key] === TYPES.boolean) {
			editingColDef = this.booleanCellCollDef;
		}

		if (TYPES_CELLS_MAP[key] === TYPES.currency) {
			editingColDef = this.currencyCellCollDef;
		}

		if (TYPES_CELLS_MAP[key] === TYPES.notEditable) {
			editingColDef = this.notEditableCellCollDef;
		}

		if (key === COLUMNS.DOCUMENT_DATE) {
			editingColDef.filterParams = {
				comparator: compareDates,
			};
		} else {
			editingColDef.filterParams = {
				textCustomComparator: customComparator,
				valueGetter: params => {
					return params.data[key] != null ? params.data[key] : '';
				},
			};
		}

		if (FILTER_TYPES[columnDef.field] === 'agSetColumnFilter') {
			const filterValues = this.getFilterValues(columnDef.field);

			if (filterValues) {
				editingColDef.filterParams = { values: filterValues };
			}
		}

		return editingColDef;
	}

	getFilterValues(field) {
		const { customFilterOptions, companiesList } = this.props;

		const filterOption = (customFilterOptions || []).find(
			({ columnName }) => field === columnName
		);

		if (filterOption) {
			return filterOption.values.map(({ value }) => value);
		}

		if (
			field === COLUMNS.ADDRESSABLE ||
			field === COLUMNS.SOURCEABLE ||
			field === COLUMNS.IsTailSpend
		) {
			return [true, false];
		}

		if (field === COLUMNS.SPEND_TYPE) {
			return ['INDIRECT', 'DIRECT'];
		}

		if (field === COLUMNS.PARENT_LEGAL_ENTITY) {
			return companiesList;
		}

		return null;
	}

	shouldComponentUpdate(nextProps, nextState) {
		if (
			this.props.filtersData &&
			(nextProps.filtersData.dateFrom !== this.props.filtersData.dateFrom ||
				nextProps.filtersData.dateTo !== this.props.filtersData.dateTo ||
				nextProps.filtersData.clientCategoryL1 !==
					this.props.filtersData.clientCategoryL1 ||
				nextProps.filtersData.clientCategoryL2 !==
					this.props.filtersData.clientCategoryL2 ||
				nextProps.filtersData.clientCategoryL3 !==
					this.props.filtersData.clientCategoryL3 ||
				nextProps.filtersData.clientCategoryL4 !==
					this.props.filtersData.clientCategoryL4 ||
				nextProps.filtersData.company !== this.props.filtersData.company ||
				nextProps.filtersData.companies !== this.props.filtersData.companies ||
				nextProps.filtersData.harmonized !==
					this.props.filtersData.harmonized ||
				!isEqual(nextProps.filtersData.filters, this.props.filtersData.filters))
		) {
			this.gridApi = null;
			this.areFiltersApplied = false;

			this.setState({ forceGridRedraw: true });

			return false;
		}

		return !this.gridApi;
	}

	onGridReady = params => {
		this.gridApi = params.api;
		this.gridColumnApi = params.columnApi;

		this.setStateFromLocalStorage();

		if (!this.areFiltersApplied) {
			this.applyContextFilters();
		}
		this.areFiltersApplied = true;

		this.props.setIsReady && this.props.setIsReady(true);

		const datasource = createServerSideDatasource();
		datasource.setFiltersData({
			...(this.props.filtersData || {}),
			isUnpublished: this.props.isUnpublished,
		});
		this.gridApi.setServerSideDatasource(datasource);
	};

	getValueFromMoreFilters = key => {
		const { filtersData } = this.props;

		const item = filtersData.filters.find(
			({ columnName }) => key === columnName
		);

		if (item) {
			return item.value;
		}

		return null;
	};

	applyContextFilters = () => {
		const { filtersData, isCustomCategory, customFilterOptions } = this.props;

		if (this.gridApi && filtersData) {
			// set one of the chart filter columns
			Object.keys(chartFilterColumns).forEach(key => {
				let filterValue =
					filtersData[key] ||
					(filtersData.filters ? this.getValueFromMoreFilters(key) : null);
				const columnName = chartFilterColumns[key];
				const instance = this.gridApi.getFilterInstance(COLUMNS[columnName]);

				if (instance) {
					if (
						key &&
						key === 'sourceable' &&
						filterValue !== undefined &&
						filterValue !== null
					) {
						instance.setModel({
							type: 'set',
							values: [filterValue ? 'true' : 'false'],
						});
					} else if (
						filterValue &&
						(key === 'company' || key === 'spendType')
					) {
						instance.setModel({
							type: 'set',
							values: Array.isArray(filterValue) ? filterValue : [filterValue],
						});
					} else if (!!filterValue) {
						if (key === 'monthYearDate') {
							const date = moment(filterValue, 'YYYY MMM');
							instance.setModel({
								type: 'contains',
								filter: date.format('YYYY-MM'),
							});
						} else if (
							key === 'isTailSpend' ||
							(key && key.indexOf('custom_data') === 0)
						) {
							const selectedCustomFilterOption = customFilterOptions.find(
								option => option.columnName === key
							);

							const transformFilterValue = filterValue => {
								if (filterValue === EMPTY_VALUE_NULL) {
									return null;
								} else if (
									filterValue === EMPTY_VALUE_SPACE ||
									filterValue === EMPTY_VALUE_KEY
								) {
									return '';
								} else if (selectedCustomFilterOption) {
									const filterValueFromOption = selectedCustomFilterOption.values.find(
										item =>
											(item.value ? item.value.toLowerCase() : item.value) ===
											(filterValue ? filterValue.toLowerCase() : filterValue)
									);
									if (filterValueFromOption) {
										return filterValueFromOption.value;
									}

									return filterValue;
								} else {
									return filterValue;
								}
							};

							if (Array.isArray(filterValue)) {
								filterValue = filterValue.map(transformFilterValue);
							} else {
								filterValue = transformFilterValue(filterValue);
							}

							instance.setModel({
								type: 'set',
								values: Array.isArray(filterValue)
									? filterValue
									: [filterValue],
							});
						} else {
							if (
								filterValue === EMPTY_VALUE_NULL ||
								filterValue === EMPTY_VALUE_SPACE ||
								filterValue === EMPTY_VALUE_KEY
							) {
								filterValue = EMPTY_VALUE;
								// NOTE: workaround - as blank string and null are not supported
								// https://github.com/ag-grid/ag-grid/issues/2712
							}

							instance.setModel({
								type: 'equals',
								filter: filterValue,
								filterTo: null,
							});
						}
					}
				}
			});

			// set custom column value
			if (filtersData.customColumnKey && filtersData.customColumnValue) {
				const key = filtersData.customColumnKey;
				if (!!key.match(/^customfilter\d+$/)) {
					const columnName = key.replace('filter', '_data');
					const instance = this.gridApi.getFilterInstance(COLUMNS[columnName]);
					const filterValue = filtersData.customColumnValue;
					if (instance) {
						instance.setModel({
							type: 'equals',
							filter: filterValue,
							filterTo: null,
						});
					}
				}
			}

			// adding in all hierarchy values as filters if they exist
			if (filtersData.hierarchy) {
				const customCategories = [
					COLUMNS.Custom_L1Category,
					COLUMNS.Custom_L2Category,
					COLUMNS.Custom_L3Category,
					COLUMNS.Custom_L4Category,
				];
				const categories = [
					COLUMNS.AP_L1Category,
					COLUMNS.AP_L2Category,
					COLUMNS.AP_L3Category,
					COLUMNS.AP_L4Category,
				];
				const hieararchyColumns = (isCustomCategory
					? customCategories
					: categories
				).map((item, index) => ({
					name: item,
					value: filtersData.hierarchy[index],
				}));

				hieararchyColumns.forEach(item => {
					if (item.value) {
						const instance = this.gridApi.getFilterInstance(item.name);

						if (instance) {
							instance.setModel({
								type: 'equals',
								filter: item.value,
								filterTo: null,
							});
						}
					}
				});
			}
			this.gridApi.onFilterChanged();
		}
	};

	resetAllFilters = () => {
		if (this.gridApi) {
			this.gridApi.setFilterModel(null);
		}
		this.updateQueryString();
	};

	getLocalStorageKey = () => {
		const { apTaxonomy } = this.props;
		return `${GRID_NAME}-GRID-COLUMNS-${apTaxonomy ? 'ap' : 'client'}`;
	};

	handleColumnsChange = ({ columnApi }) => {
		if (!columnApi || !this.areFiltersApplied) {
			return;
		}

		const columnState = columnApi.getColumnState();

		setLocalStorageItem(this.getLocalStorageKey(), columnState);
	};

	setDataToRows = data => {
		if (this.gridApi) {
			data.forEach(rowData => {
				const rowNode = this.gridApi.getRowNode(rowData.ID);
				const flashColumns = [];

				Object.keys(rowNode.data).forEach(key => {
					if (rowNode.data[key] !== rowData[key]) {
						flashColumns.push(key);
					}
				});

				this.gridApi.flashCells({
					rowNodes: [rowNode],
					columns: flashColumns,
				});
				rowNode.setData(rowData);
			});
		}
	};

	isExternalFilterPresent = () => {
		return true;
	};

	deselectAllRows = () => {
		if (this.gridApi) {
			this.gridApi.deselectAll();
		}
	};

	handleFilterChange = () => {
		this.deselectAllRows();
	};

	updateQueryString = () => {
		if (this.props.contextParams) {
			let obj = {};
			Object.keys(chartFilterColumns).forEach(k => {
				obj[k] = null;
			});
			obj['customColumnKey'] = null;
			obj['customColumnValue'] = null;
			// clear query params
			this.props.contextParams.setQueryStringStateAndReplace(obj);
		}
	};

	getCurrentColumnsState = () => {
		const { apTaxonomy } = this.props;
		const defaultColumnState = apTaxonomy
			? AP_DEFAULT_COLUMN_STATE
			: CLIENT_DEFAULT_COLUMN_STATE;
		let currentState =
			getLocalStorageItem(this.getLocalStorageKey()) || defaultColumnState;
		return currentState;
	};

	setStateFromLocalStorage = () => {
		const sortState = getLocalStorageItem(GRID_NAME + '-GRID-SORT');
		const colState = this.getCurrentColumnsState();

		if (colState !== null && this.gridColumnApi !== null) {
			this.gridColumnApi.setColumnState(colState);
		}

		if (sortState !== null && this.gridApi !== null) {
			this.gridApi.setSortModel(sortState);
		}
	};

	handleSortingChange = ({ api }) => {
		if (!api) {
			return;
		}

		const sortModel = api.getSortModel();
		setLocalStorageItem(GRID_NAME + '-GRID-SORT', sortModel);
	};

	getRowNodeId = data => data.ID;

	/**
	 * Render out the ag grid after the data state property has been set
	 */
	render() {
		const {
			columnDefs,
			defaultColDef,
			rowSelection,
			suppressRowClickSelection,
			forceGridRedraw,
		} = this.state;

		if (!columnDefs) {
			return null;
		}

		if (forceGridRedraw) {
			setTimeout(() => this.setState({ forceGridRedraw: false }));

			return null;
		}

		const gridOptions = {
			modules: AllModules,
			cacheBlockSize: 200,
			maxBlocksInCache: 10,
			enableColResize: true,
			enableFilter: true,
			sideBar: true,
			rowModelType: 'serverSide',
			columnDefs,
			defaultColDef,
			rowSelection,
			suppressRowClickSelection,
			floatingFilter: true,
			maxConcurrentDatasourceRequests: 1,
			suppressCellSelection: true,

			getRowNodeId: this.getRowNodeId,
			onColumnEverythingChanged: this.handleColumnsChange,
			onColumnMoved: this.handleColumnsChange,
			onColumnResized: this.handleColumnsChange,
			onColumnVisible: this.handleColumnsChange,
			onColumnPinned: this.handleColumnsChange,
			onColumnRowGroupChanged: this.handleColumnsChange,
			onColumnGroupOpened: this.handleColumnsChange,
			onSortChanged: this.handleSortingChange,
			onGridReady: this.onGridReady,
			isExternalFilterPresent: this.isExternalFilterPresent,
			onFilterChanged: this.handleFilterChange,
		};

		return (
			<Wrapper>
				<div style={WRAPPER_THEME_STYLES} className="ag-theme-balham">
					<AgGridReact {...gridOptions} />
				</div>
			</Wrapper>
		);
	}
}
